import { useMemo } from "react";
import { ErrorBoundary } from "../error-boundary";
import { useWidgetRegistry } from "./registry-context";
import type { WidgetConfig } from "./types";
import type { WidgetModule } from "./widget-module";
import { UnknownWidgetBody } from "./placeholder";
import { WidgetError } from "./states/widget-error";
import type { Omittable } from "../../lib/props";

export type WidgetProps = Omittable<{
  /** A registered `type` key to look up, or a `WidgetModule` to run directly (nesting). */
  module: WidgetModule<any> | string;
  config?: WidgetConfig;
  schemaVersion?: number;
  /** The widget's live grid span (cells). Nested widgets pass their sub-region size. */
  w: number;
  h: number;
  onConfigChange?: (patch: WidgetConfig) => void;
}>;

/**
 * THE widget harness. It runs one widget: resolves its config (schema-on-read) and
 * renders the widget's BODY inside an error boundary. The body reads its own live
 * grid span (`w`/`h`) to decide what to show, and fetches its OWN data — the
 * harness supplies none, and knows nothing about data scope.
 *
 * The registry comes from {@link WidgetRegistryProvider} context. The `resolved`
 * config memo is load-bearing: it absorbs zod's fresh-object-per-`parse` so the
 * config object a widget keys its query variables on stays reference-stable.
 */
export function Widget({ module, config, schemaVersion, w, h, onConfigChange }: WidgetProps) {
  const registry = useWidgetRegistry();
  const def = typeof module === "string" ? registry.get(module) : module;
  const resolved = useMemo(
    () => (def ? registry.resolveConfig(def, config, schemaVersion ?? 1) : {}),
    [registry, def, config, schemaVersion],
  );

  if (!def) return <UnknownWidgetBody type={String(module)} />;

  const Render = def.render;
  return (
    <ErrorBoundary
      fallback={({ error, reset }) => <WidgetError error={error} onRetry={reset} />}
      // Keyed on type + config so a widget stuck on a transient render error
      // auto-recovers when its settings change (in addition to the manual retry).
      resetKey={`${def.type}:${JSON.stringify(resolved)}`}
    >
      <Render w={w} h={h} config={resolved} onConfigChange={onConfigChange} />
    </ErrorBoundary>
  );
}
